<template>
  <div class="funnel-lead bg-white">

    <!-- ── Hero + waardepropositie ───────────────────────── -->
    <section class="bg-[#111] text-white">
      <div class="max-w-screen-xl mx-auto px-6 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <p class="text-[0.875rem] font-semibold text-[#FFD500] mb-4 uppercase tracking-[0.03em]">
            <span aria-hidden="true">💡</span> Gratis & vrijblijvend
          </p>
          <h1 class="text-white text-[clamp(2rem,5vw,3.25rem)] font-black leading-[1.1] mb-5">
            Gratis energieadvies op maat
          </h1>
          <p class="text-[1.0625rem] sm:text-[1.1875rem] leading-[1.7] text-[#ccc] mb-8 max-w-[520px]">
            Balkon zonnepanelen of een thuisbatterij? Laat je gegevens achter en onze
            energiespecialisten rekenen gratis uit wat het beste past bij jouw situatie -
            eerlijk en zonder verkooppraatje.
          </p>

          <ul class="flex flex-col gap-3 list-none p-0 m-0">
            <li v-for="(b, i) in benefits" :key="i" class="flex items-center gap-3 text-[1rem] text-[#eee]">
              <span class="w-7 h-7 rounded-full bg-[#FFD500] text-[#111] flex items-center justify-center shrink-0">
                <i class="bi bi-check-lg text-[0.9rem]"></i>
              </span>
              {{ b }}
            </li>
          </ul>
        </div>

        <!-- Formulier-kaart -->
        <div class="bg-[#1e1e1e] rounded-[20px] p-6 sm:p-8 lg:p-10">
          <h2 class="text-[1.5rem] font-black text-white mb-2">Vraag je advies aan</h2>
          <p class="text-[0.95rem] text-[#aaa] mb-6">We reageren binnen 1 werkdag.</p>

          <form class="flex flex-col gap-4" @submit.prevent="onSubmit" novalidate>
            <input v-model="hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="hidden" />
            <input
              v-model="name" type="text" autocomplete="name" placeholder="Je naam"
              aria-label="Naam"
              class="h-[52px] px-4 rounded-[10px] bg-white text-[#111] text-[1rem] placeholder:text-[#888]
                     outline-none border-2 border-transparent focus:border-[#FFD500] transition-colors"
            />
            <input
              v-model="email" type="email" required autocomplete="email" placeholder="Je e-mailadres"
              aria-label="E-mailadres"
              class="h-[52px] px-4 rounded-[10px] bg-white text-[#111] text-[1rem] placeholder:text-[#888]
                     outline-none border-2 border-transparent focus:border-[#FFD500] transition-colors"
            />
            <div class="flex h-[52px] rounded-[10px] bg-white overflow-hidden border-2 border-transparent focus-within:border-[#FFD500] transition-colors">
              <span class="flex items-center gap-2 pl-4 pr-3 border-r border-[#e5e5e5] shrink-0 select-none">
                <span aria-hidden="true">🇳🇱</span>
                <span class="text-[#111] font-semibold text-[0.95rem]">+31</span>
              </span>
              <input
                v-model="phone" type="tel" inputmode="tel" autocomplete="tel" placeholder="Mobiel nummer"
                aria-label="Telefoonnummer"
                class="flex-1 min-w-0 px-4 bg-transparent text-[#111] text-[1rem] placeholder:text-[#888] outline-none"
              />
            </div>

            <label class="flex items-center gap-2 text-[0.9rem] text-[#aaa] cursor-pointer">
              <span class="text-[#aaa]">Waar ben je in geïnteresseerd?</span>
            </label>
            <div class="flex gap-3 -mt-2">
              <label v-for="opt in interests" :key="opt" class="flex-1">
                <input type="radio" name="interest" :value="opt" v-model="interest" class="peer sr-only" />
                <span class="block text-center text-[0.9rem] py-2.5 rounded-[10px] bg-white/5 text-[#ccc] border-2 border-white/10
                             cursor-pointer peer-checked:border-[#FFD500] peer-checked:text-white peer-checked:bg-[#FFD500]/10 transition-colors">
                  {{ opt }}
                </span>
              </label>
            </div>

            <label class="flex items-start gap-3 text-[0.85rem] text-[#aaa] leading-[1.5] cursor-pointer mt-1">
              <input v-model="agreed" type="checkbox" class="mt-[3px] h-[18px] w-[18px] shrink-0 accent-[#FFD500] cursor-pointer" />
              <span>
                Ik ga akkoord met het
                <router-link to="/privacy" class="text-[#FFD500] underline underline-offset-2 hover:text-[#ffb800]">privacybeleid</router-link>.
              </span>
            </label>

            <p v-if="error" class="text-[0.875rem] text-[#ff6b6b] -mt-1" role="alert">{{ error }}</p>
            <p v-if="success" class="text-[0.875rem] text-[#4ade80] -mt-1" role="status">
              Bedankt! We nemen binnen 1 werkdag contact met je op.
            </p>

            <button type="submit" :disabled="submitting"
              class="mt-1 h-[54px] rounded-[10px] bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold text-[1.0625rem]
                     funnel-lead-submit
                     disabled:opacity-60 disabled:cursor-not-allowed">
              {{ submitting ? 'Bezig…' : 'Vraag gratis advies aan' }}
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- ── Persoonlijk advies ────────────────────────────── -->
    <section class="bg-white py-14 lg:py-20 border-b border-[#eee]">
      <div class="max-w-screen-xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div class="order-2 md:order-1">
          <p class="text-[0.8125rem] font-semibold text-[#b58a00] mb-3 uppercase tracking-[0.06em]">Persoonlijk advies</p>
          <h2 class="text-[clamp(1.5rem,3.5vw,2.25rem)] font-black text-[#111] leading-[1.15] mb-4">
            Advies van echte mensen, geen chatbot
          </h2>
          <p class="text-[1.0625rem] leading-[1.7] text-[#555] mb-6 max-w-[520px]">
            Je krijgt een persoonlijk antwoord van iemand die de producten zelf gebruikt. We denken
            mee over wat past bij jouw dak, balkon of meterkast - en zeggen het eerlijk als iets níét
            de moeite waard is.
          </p>
          <ul class="flex flex-col gap-2.5 list-none p-0 m-0 text-[0.975rem] text-[#333]">
            <li class="flex items-center gap-2.5"><i class="bi bi-telephone text-[#FFD500]"></i> Bereikbaar via telefoon, mail en live chat</li>
            <li class="flex items-center gap-2.5"><i class="bi bi-box-seam text-[#FFD500]"></i> We gebruiken de producten die we verkopen</li>
            <li class="flex items-center gap-2.5"><i class="bi bi-hand-thumbs-up text-[#FFD500]"></i> Eerlijk advies, ook als je beter kunt wachten</li>
          </ul>
        </div>

        <div class="order-1 md:order-2 flex justify-center">
          <div class="rounded-[20px] bg-[#f2f2f2] overflow-hidden max-w-[380px] w-full">
            <img
              :src="'/wp-content/uploads/2026/07/thomas_batterij-removebg-preview.png?w=760'"
              alt="Plug2Charge-adviseur met een draagbare thuisbatterij"
              loading="lazy" width="425" height="586"
              class="block w-full h-auto object-contain"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ── Social proof strip ────────────────────────────── -->
    <section class="bg-[#fafafa] py-10 border-b border-[#eee]">
      <div class="max-w-screen-xl mx-auto px-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[0.9rem] text-[#555]">
        <span class="flex items-center gap-2"><i class="bi bi-star-fill text-[#FFD500]"></i> 4,8/5 klantbeoordeling</span>
        <span class="flex items-center gap-2"><i class="bi bi-people text-[#FFD500]"></i> 5.000+ klanten geholpen</span>
        <span class="flex items-center gap-2"><i class="bi bi-shield-check text-[#FFD500]"></i> Onafhankelijk advies</span>
        <span class="flex items-center gap-2"><i class="bi bi-clock text-[#FFD500]"></i> Reactie binnen 1 werkdag</span>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const benefits = [
  'Persoonlijke berekening van opbrengst en terugverdientijd',
  'Advies over vergunningen en subsidies',
  'Geen verplichtingen - puur om je op weg te helpen',
]
const interests = ['Balkon zonnepanelen', 'Thuisbatterij', 'Weet ik nog niet']

const name = ref('')
const email = ref('')
const phone = ref('')
const hp = ref('')            // honeypot
const interest = ref('')
const agreed = ref(false)
const error = ref('')
const success = ref(false)

const submitting = ref(false)

async function onSubmit() {
  error.value = ''
  success.value = false
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())
  if (!emailOk) { error.value = 'Vul een geldig e-mailadres in.'; return }
  if (!agreed.value) { error.value = 'Ga akkoord met het privacybeleid om verder te gaan.'; return }

  submitting.value = true
  try {
    const res = await fetch('/api/newsletter-subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.value.trim(),
        email: email.value.trim(),
        phone: phone.value.trim(),
        interest: interest.value,
        source: 'energieadvies',
        website: hp.value,
      }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Aanvragen mislukt, probeer het opnieuw.')
    }
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', { method: 'energieadvies_funnel' })
    }
    success.value = true
    name.value = ''; email.value = ''; phone.value = ''; interest.value = ''; agreed.value = false
  } catch (e) {
    error.value = e.message
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.funnel-lead-submit {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
              box-shadow 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.funnel-lead-submit:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.45);
}
.funnel-lead-submit:not(:disabled):active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) { .funnel-lead-submit { transition: none; } }
</style>
